import { useCallback, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Typography,
  CircularProgress,
  Pagination as MuiPagination,
  SxProps,
  Theme,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  IconButton,
} from '@mui/material';

import { MajorsExplorerInstructionsModal } from '../components/MajorsExplorerInstructionsModal';
import { MultiSelectSearchList } from '../components/MultiSelectSearchList';
import { RankedResultsList } from '../components/RankedResultsList';
import { RelevanceBadge } from '../components/RelevanceBadge';
import { RelevanceFilters } from '../components/RelevanceFilters';
import { WhyWeBuiltThisToolModal } from '../components/WhyWeBuiltThisToolModal';
import { RelevanceMode, RelevanceRating, RelevanceResultItem, SelectableItem } from '../types';

const RELEVANCE_STRINGS = appStrings.modules.majorsGuide.careerRelevance.relevanceLevelsModal;

/**
 * Extended props for CareerRelevancePage including modal state
 */
interface CareerRelevancePageProps {
  /** Current mode - 'paths' or 'majors' */
  mode: RelevanceMode;
  /** Available items for selection */
  availableItems: SelectableItem[];
  /** Currently selected item names */
  selectedItems: string[];
  /** Visible relevance levels for filtering */
  visibleLevels: Set<RelevanceRating>;
  /** Ranked results */
  results: RelevanceResultItem[];
  /** Total number of results */
  total: number;
  /** Current page number */
  page: number;
  /** Loading state */
  isLoading: boolean;
  /** Items loading state */
  isItemsLoading: boolean;
  /** Callback when mode changes */
  onModeChange: (mode: RelevanceMode) => void;
  /** Callback when selections change */
  onSelectionChange: (items: string[]) => void;
  /** Callback when a level filter is toggled */
  onLevelToggle: (level: RelevanceRating) => void;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when a result is clicked */
  onResultClick: (item: RelevanceResultItem) => void;
  /** Number of items per page */
  perPage: number;
  /** Callback when "Clear all" relevance levels is clicked */
  onResetLevels?: () => void;
}

/**
 * CareerRelevancePage - Presentational component for exploring major-career relevance
 *
 * Features:
 * - Left-aligned header with "Instructions" button on desktop
 * - Pill-style mode tabs (Career Paths ↔ Majors, Majors ↔ Career Paths)
 * - Stacked card layout (selection card, results card)
 * - RelevanceFilters with colored badges and "Clear all" button
 * - "Understanding Relevance Levels" button opens explanatory modal
 * - "Instructions" button opens "Why We Created This Tool" modal
 */
export const CareerRelevancePage = ({
  mode,
  availableItems,
  selectedItems,
  visibleLevels,
  results,
  total,
  page,
  isLoading,
  isItemsLoading,
  onModeChange,
  onSelectionChange,
  onLevelToggle,
  onPageChange,
  onResultClick,
  perPage,
  onResetLevels,
}: CareerRelevancePageProps) => {
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [whyWeBuiltOpen, setWhyWeBuiltOpen] = useState(false);
  const [relevanceLevelsOpen, setRelevanceLevelsOpen] = useState(false);

  const handleModeToggle = useCallback(() => {
    onModeChange(mode === 'paths' ? 'majors' : 'paths');
  }, [mode, onModeChange]);

  const handlePaginationChange = useCallback(
    (_event: React.ChangeEvent<unknown>, newPage: number) => {
      onPageChange(newPage);
    },
    [onPageChange],
  );

  const totalPages = Math.ceil(total / perPage);

  // Get labels based on mode
  const selectionLabel = mode === 'paths' ? 'Career Paths' : 'Majors';
  const resultsLabel = mode === 'paths' ? 'College Majors' : 'Career Paths';
  const resultsLabelLower = mode === 'paths' ? 'majors' : 'career paths';

  const toggleButtonLabel =
    mode === 'paths'
      ? { from: 'Majors', to: 'Career Paths' }
      : { from: 'Career Paths', to: 'Majors' };

  const pageTitleLabel =
    mode === 'paths'
      ? { from: 'Career Paths', to: 'Majors' }
      : { from: 'Majors', to: 'Career Paths' };

  return (
    <Box sx={containerSx}>
      {/* Header */}
      <Box sx={headerSx}>
        <Box sx={headerContentSx}>
          <Box sx={headerTextSx}>
            <Typography variant="h4" sx={titleSx}>
              {pageTitleLabel.from}{' '}
              <AppIcon iconName="ArrowRight" diameter="24px" color="currentColor" />{' '}
              {pageTitleLabel.to} Relevance
            </Typography>
            <Typography variant="body1" sx={descriptionSx}>
              Discover the relevance between college majors and career paths with detailed ratings
              and expert rationales.
            </Typography>
          </Box>
          <Box sx={headerButtonsSx}>
            <Typography
              component="span"
              onClick={() => setWhyWeBuiltOpen(true)}
              sx={whyWeBuiltLinkSx}
            >
              {appStrings.modules.majorsGuide.careerRelevance.whyWeBuiltThisTool}
            </Typography>
            <Button
              id="career-relevance-instructions-btn"
              variant="contained"
              size="md"
              hierarchy="Secondary gray"
              onClick={() => setInstructionsOpen(true)}
              sx={headerButtonSx}
            >
              Instructions
            </Button>
            <Button
              id="career-relevance-mode-toggle-btn"
              variant="contained"
              size="md"
              hierarchy="Primary"
              onClick={handleModeToggle}
              sx={headerButtonSx}
            >
              <AppIcon iconName="RefreshCcw03" diameter="20px" color="currentColor" />
              Switch to {toggleButtonLabel.from}{' '}
              <AppIcon iconName="ArrowRight" diameter="16px" color="currentColor" />{' '}
              {toggleButtonLabel.to}
            </Button>
          </Box>
        </Box>
        <Divider sx={headerDividerSx} />
      </Box>

      {/* Stacked Cards Layout */}
      <Box sx={contentContainerSx}>
        {/* Selection Card */}
        <Card sx={cardSx}>
          <CardContent sx={cardContentSx}>
            <Typography variant="h6" sx={cardTitleSx}>
              Select {selectionLabel}
            </Typography>

            {isItemsLoading ? (
              <Box sx={loadingContainerSx}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <MultiSelectSearchList
                key={mode}
                items={availableItems}
                selectedItems={selectedItems}
                onSelectionChange={onSelectionChange}
                placeholder={`Search ${selectionLabel.toLowerCase()}...`}
                maxSelections={5}
                emptyMessage={`No ${selectionLabel.toLowerCase()} found`}
              />
            )}
          </CardContent>
        </Card>

        {/* Relevance Filters - Separate Section */}
        <Box sx={filtersContainerSx}>
          <Box sx={{ flex: '1 1 auto' }}>
            <RelevanceFilters
              visibleRatings={visibleLevels}
              onToggle={onLevelToggle}
              onClearAll={onResetLevels}
            />
          </Box>
          <Button
            id="career-relevance-levels-btn"
            variant="contained"
            size="md"
            hierarchy="Secondary gray"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => setRelevanceLevelsOpen(true)}
            sx={relevanceLevelsButtonSx}
          >
            {RELEVANCE_STRINGS.title}
          </Button>
        </Box>

        {/* Results Section - no card wrapper, matches /search/majors */}
        <Box sx={resultsSectionSx}>
          {/* Results count - simple text like "252 majors found" */}
          {selectedItems.length > 0 && total > 0 && (
            <Typography sx={resultsCountTextSx}>
              {total} {resultsLabelLower} found
            </Typography>
          )}

          {selectedItems.length === 0 ? (
            <Box sx={emptyStateSx}>
              <Typography variant="body1" sx={emptyStateTextSx}>
                Select some {mode === 'paths' ? 'paths' : 'majors'} to see results
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box sx={loadingContainerSx}>
              <CircularProgress />
            </Box>
          ) : results.length === 0 ? (
            <Box sx={emptyStateSx}>
              <Typography variant="body1" color="text.secondary">
                No {resultsLabel.toLowerCase()} found matching your criteria.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filter settings.
              </Typography>
            </Box>
          ) : (
            <>
              <RankedResultsList
                results={results}
                mode={mode === 'paths' ? 'majors' : 'paths'}
                selections={selectedItems}
                onItemClick={onResultClick}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={paginationContainerSx}>
                  <MuiPagination
                    count={totalPages}
                    page={page}
                    onChange={handlePaginationChange}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Instructions Carousel Modal */}
      <MajorsExplorerInstructionsModal
        open={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
      />

      {/* Why We Built This Tool Modal */}
      <WhyWeBuiltThisToolModal open={whyWeBuiltOpen} onClose={() => setWhyWeBuiltOpen(false)} />

      {/* Understanding Relevance Levels Dialog */}
      <Dialog
        open={relevanceLevelsOpen}
        onClose={() => setRelevanceLevelsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: relevanceLevelsDialogPaperSx }}
      >
        <Box sx={relevanceLevelsDialogContainerSx}>
          {/* Header */}
          <Box sx={relevanceLevelsHeaderSx}>
            <Box sx={relevanceLevelsHeaderTextSx}>
              <Typography sx={relevanceLevelsDialogTitleSx}>{RELEVANCE_STRINGS.title}</Typography>
              <Typography sx={relevanceLevelsDialogSubtitleSx}>
                {RELEVANCE_STRINGS.subtitle}
              </Typography>
            </Box>
            <IconButton
              id="career-relevance-close-button"
              onClick={() => setRelevanceLevelsOpen(false)}
              sx={relevanceLevelsCloseButtonSx}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content - Relevance Level Cards */}
          <Box sx={relevanceLevelCardsContainerSx}>
            <RelevanceLevelCard
              level="High"
              title={RELEVANCE_STRINGS.highTitle}
              description={RELEVANCE_STRINGS.highDescription}
            />

            <RelevanceLevelCard
              level="Medium"
              title={RELEVANCE_STRINGS.mediumTitle}
              description={RELEVANCE_STRINGS.mediumDescription}
            />

            <RelevanceLevelCard
              level="Low"
              title={RELEVANCE_STRINGS.lowTitle}
              description={RELEVANCE_STRINGS.lowDescription}
            />
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

// Local component for relevance level explanation cards
const RelevanceLevelCard = ({
  level,
  title,
  description,
}: {
  level: RelevanceRating;
  title: string;
  description: string;
}) => (
  <Box sx={relevanceLevelCardSx}>
    <Box sx={relevanceLevelCardContentSx}>
      <Box sx={relevanceLevelIconCircleSx(level.toLowerCase() as 'high' | 'medium' | 'low')}>
        <AppIcon iconName="Hurricane01" diameter="20px" color="Colors/Base/White" />
      </Box>
      <Box sx={relevanceLevelCardTextSx}>
        <Box sx={relevanceLevelTitleRowSx}>
          <Typography sx={relevanceLevelTitleSx}>{title}</Typography>
          <RelevanceBadge level={level} />
        </Box>
        <Typography sx={relevanceLevelDescriptionSx}>{description}</Typography>
      </Box>
    </Box>
  </Box>
);

// Styles

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  mb: backrs.spacing['spacing-8xl'],
  '@container (min-width: 900px)': {
    mb: backrs.spacing['spacing-9xl'],
  },
  width: '100%',
});

// Header styles
const headerSx: SxProps<Theme> = {
  // Container handles maxWidth and centering
  // No additional horizontal padding needed - container handles it
  pb: 2,
};

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-3xl'],
  mb: backrs.spacing['spacing-3xl'],
});

const headerTextSx: SxProps<Theme> = {
  textAlign: 'left',
  flex: 1,
};

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  mb: 1,
  fontWeight: 700,
  fontSize: '1.5rem',
  '@container (min-width: 900px)': {
    fontSize: '1.875rem',
  },
  color: backrs.colors['Colors/Text/text-primary'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  fontSize: '1.125rem',
  color: backrs.colors['Colors/Text/text-secondary'],
});

const headerDividerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Border/border-secondary'],
});

const headerButtonsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'center',
  '@container (min-width: 600px)': {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  '@container (min-width: 900px)': {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

const headerButtonSx: SxProps<Theme> = {
  whiteSpace: 'nowrap',
};

const whyWeBuiltLinkSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Brand/500'],
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});

// Card styles
const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  boxShadow: 'none',
});

const cardContentSx: SxProps<Theme> = {
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  '@container (min-width: 600px)': {
    p: 3,
  },
  '&:last-child': { pb: 2, '@container (min-width: 600px)': { pb: 3 } },
};

const cardTitleSx: SxProps<Theme> = ({ backrs }) => ({
  fontWeight: 600,
  color: backrs.colors['Colors/Text/text-primary'],
});

const filtersContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-md'],
});

const relevanceLevelsButtonSx: SxProps<Theme> = {
  whiteSpace: 'nowrap',
};

// Results section styles - no card wrapper, matches /search/majors
const resultsSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const resultsCountTextSx: SxProps<Theme> = ({ backrs }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  color: backrs.colors['Colors/Text/text-secondary'],
});

const loadingContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: 4,
};

const emptyStateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: 8,
  gap: 1,
  textAlign: 'center',
};

const emptyStateTextSx: SxProps<Theme> = {
  color: '#3b82f6',
};

const paginationContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  pt: backrs.spacing['spacing-xl'],
});

// Understanding Relevance Levels dialog styles
const relevanceLevelsDialogPaperSx: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-xl'],
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const relevanceLevelsDialogContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const relevanceLevelsHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  px: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-2xl'],
});

const relevanceLevelsHeaderTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  flex: 1,
  pr: backrs.spacing['spacing-xl'],
});

const relevanceLevelsDialogTitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-lg-semibold'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const relevanceLevelsDialogSubtitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const relevanceLevelsCloseButtonSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Foreground/fg-quaternary'],
  p: 0,
});

const relevanceLevelCardsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  px: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-3xl'],
});

const relevanceLevelCardSx: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const relevanceLevelCardContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  p: backrs.spacing['spacing-lg'],
  pr: backrs.spacing['spacing-2xl'],
});

const relevanceLevelIconCircleSx =
  (level: 'high' | 'medium' | 'low'): SxProps<Theme> =>
  ({ backrs }) => {
    const colorMap = {
      high: backrs.colors['Success/500'],
      medium: '#FEC84B', // Warning/300 from spec
      low: backrs.colors['Error/400'],
    };

    return {
      width: '40px',
      height: '40px',
      minWidth: '40px',
      borderRadius: '50%',
      backgroundColor: colorMap[level],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  };

const relevanceLevelCardTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
});

const relevanceLevelTitleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  flexWrap: 'wrap',
});

const relevanceLevelTitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-lg-semibold'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const relevanceLevelDescriptionSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});
